// Menu das três formas de passar a conversa adiante. Antes elas viviam
// espalhadas — uma no botão "Transferir", duas escondidas no ⋮ — e cada uma
// abria um popup separado. Agora um clique abre as opções que ELE pode fazer, e
// o seletor da fila/colega/número aparece NO PRÓPRIO menu, sem pular de tela.
//
// Cada opção só aparece se for possível pra ele: a Alice, que atende um canal
// só, não vê "outro número"; conversa sem fila não mostra "passar pra colega".
import React, { useEffect, useState } from 'react';
import backdropClose from '../helpers/backdropClose';

const VERDE = '#3D8B37';

function Opcao({ icone, titulo, descricao, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', gap: 12, alignItems: 'center', textAlign: 'left',
        width: '100%', background: 'transparent', color: 'var(--portal-text)',
        border: '1px solid var(--portal-border)', borderRadius: 10, padding: '12px 14px',
        cursor: 'pointer',
      }}
    >
      <span style={{
        width: 38, height: 38, flexShrink: 0, borderRadius: 9,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(61,139,55,0.14)', color: VERDE, fontSize: '1.15rem',
      }}>
        <i className={`bi ${icone}`} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ fontWeight: 600, fontSize: '0.9rem', display: 'block' }}>{titulo}</span>
        <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>{descricao}</span>
      </span>
    </button>
  );
}

// Passo 2: o seletor da opção escolhida, dentro do mesmo modal.
function Seletor({ modo, onVoltar, onFeito }) {
  const [opcoes, setOpcoes] = useState(null); // null = carregando
  const [valor, setValor] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    modo.carregarOpcoes()
      .then((res) => {
        if (!vivo) return;
        setOpcoes(res.opcoes);
        if (res.padrao !== undefined) setValor(String(res.padrao));
      })
      .catch(() => { if (vivo) { setOpcoes([]); setErro('Não deu pra carregar as opções.'); } });
    return () => { vivo = false; };
  }, [modo]);

  const confirmar = async () => {
    setEnviando(true);
    try {
      await modo.confirmar(valor);
      onFeito();
    } catch (e) {
      setErro(e?.message || 'Não deu pra transferir.');
      setEnviando(false);
    }
  };

  const bloqueado = enviando || (modo.exigeValor && !valor);

  return (
    <>
      <button
        type="button"
        onClick={onVoltar}
        style={{
          background: 'transparent', border: 'none', color: 'var(--portal-muted)',
          padding: 0, marginBottom: 6, fontSize: '0.8rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 5,
        }}
      >
        <i className="bi bi-chevron-left" /> voltar
      </button>

      <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 2 }}>{modo.titulo}</div>
      {modo.ajuda && (
        <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginBottom: 8 }}>{modo.ajuda}</div>
      )}

      {opcoes === null && (
        <div style={{ color: 'var(--portal-muted)', fontSize: '0.85rem', padding: '8px 0' }}>Carregando…</div>
      )}

      {opcoes !== null && opcoes.length === 0 && (
        <div style={{ color: 'var(--portal-muted)', fontSize: '0.85rem', padding: '8px 0' }}>
          {erro || modo.vazio || 'Nada disponível.'}
        </div>
      )}

      {opcoes !== null && opcoes.length > 0 && (
        <>
          <select
            className="form-select form-select-sm"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            style={{ marginBottom: 10 }}
          >
            {modo.placeholder && <option value="">{modo.placeholder}</option>}
            {opcoes.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
          </select>
          {erro && <div style={{ color: '#e0736f', fontSize: '0.8rem', marginBottom: 8 }}>{erro}</div>}
          <button
            type="button"
            onClick={confirmar}
            disabled={bloqueado}
            style={{
              width: '100%', background: VERDE, color: '#fff', border: 'none',
              borderRadius: 8, padding: '9px 0', fontSize: '0.88rem', fontWeight: 600,
              cursor: enviando ? 'wait' : 'pointer', opacity: bloqueado ? 0.6 : 1,
            }}
          >
            {modo.rotuloConfirmar || 'Transferir'}
          </button>
        </>
      )}
    </>
  );
}

export default function MenuTransferir({ aberto, onFechar, modos }) {
  const [modoAtivo, setModoAtivo] = useState(null);

  if (!aberto) return null;

  const fechar = () => { setModoAtivo(null); onFechar(); };
  const disponiveis = (modos || []).filter((m) => m.disponivel);

  return (
    <div
      {...backdropClose(fechar)}
      style={{
        position: 'fixed', inset: 0, zIndex: 20040, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--portal-surface)', color: 'var(--portal-text)',
          border: '1px solid var(--portal-border)', borderRadius: 12,
          padding: 16, width: '100%', maxWidth: 380,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        {modoAtivo ? (
          <Seletor modo={modoAtivo} onVoltar={() => setModoAtivo(null)} onFeito={fechar} />
        ) : (
          <>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 2 }}>
              Passar esta conversa
            </div>
            {disponiveis.map((m) => (
              <Opcao
                key={m.key}
                icone={m.icone}
                titulo={m.titulo}
                descricao={m.descricao}
                onClick={() => setModoAtivo(m)}
              />
            ))}
            <button
              type="button"
              onClick={fechar}
              style={{
                background: 'transparent', color: 'var(--portal-muted)', border: 'none',
                padding: '8px 0 2px', fontSize: '0.82rem', cursor: 'pointer',
              }}
            >
              cancelar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
